import { useNavigate, useSearchParams } from 'react-router';
import { Link } from '#components/atoms/link/Link.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { PageCloser } from '#components/patterns/page-closer/PageCloser.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { UpsellModal } from '#components/patterns/upsell-modal/UpsellModal.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { usePersistedNetworkFilter } from '#hooks/usePersistedNetworkFilter.tsx';
import { useSuspenseIsHostsLimitReached } from '#screens/hosts/add/useSuspenseIsHostsLimitReached.ts';
import { hostsListTo } from '#screens/hosts/listLocation.ts';
import { HOST_KIND_LABELS, isHostKind } from '#util/host.ts';
import { fromArray, Titled } from '#util/title.ts';
import { HostForm } from '../form/HostForm.tsx';
import { HostsPage } from '../hosts-page/HostsPage.tsx';

/**
 * {@link AddHostPage} shows a form for adding a host of any kind. The `type` query param picks the
 * kind (`?type=lighthouse` / `?type=relay`); anything else — including its absence — means a plain
 * host.
 */
export function AddHostPage() {
  const navigate = useNavigate();
  const location = useAppLocation();
  const [searchParams] = useSearchParams();
  const from = location.state?.from;
  const { isHostsLimitReached } = useSuspenseIsHostsLimitReached();
  const persistedNetworkFilter = usePersistedNetworkFilter();

  const typeParam = searchParams.get('type');
  const kind = typeParam !== null && isHostKind(typeParam) ? typeParam : 'host';
  const listTo = hostsListTo(kind, persistedNetworkFilter);

  function returnToList() {
    void navigate(from ?? listTo);
  }

  function setEnrollingHostId(hostId: string) {
    void navigate(`../${hostId}/enroll`, {
      state: { from },
      replace: true,
    });
  }

  return (
    <Titled title={(title) => fromArray([`Add ${HOST_KIND_LABELS[kind].toLowerCase()}`, title])}>
      {isHostsLimitReached ?
        <>
          <HostsPage />
          <UpsellModal onClose={returnToList} />
        </>
      : <MainLayout
          header={
            <PageHeader
              title={
                <>
                  <Link to={listTo} className="a">
                    Hosts
                  </Link>
                  {' / '}
                  Add {kind === 'host' ? '' : kind}
                </>
              }
            >
              <PageCloser fallbackTo={listTo} />
            </PageHeader>
          }
        >
          <HostForm onSuccess={setEnrollingHostId} hostId={null} kind={kind} />
        </MainLayout>
      }
    </Titled>
  );
}
